import type { FieldIssue } from "@app/core/validation";
import type { Lang } from "../i18n.js";
import { ApiError } from "../api/client.js";
import {
  REASON_MESSAGES,
  CODE_MESSAGES,
  GENERIC_MESSAGE,
  RULE_MESSAGES,
  FIELD_MESSAGES,
  FIELD_LABELS,
  type ErrorMessage,
  type ErrorAction,
} from "./catalog.js";

/** A fully-resolved, ready-to-render error. */
export interface ResolvedError {
  title: string;
  hint?: string;
  action?: ErrorAction;
  /** Per-field messages, when the error carried validation issues. */
  fieldErrors: Record<string, string>;
  /** Support reference (correlation id), when present. */
  correlationId?: string;
  /** The granular reason (or code) that produced this message — handy for tests/telemetry. */
  reason?: string;
}

interface ErrorDetails {
  issues?: FieldIssue[];
  retryAfterSeconds?: number;
}

/**
 * Translate any thrown error into a localized, actionable `ResolvedError`.
 * Resolution order: server `reason` → coarse `code` → generic fallback. A
 * non-ApiError (an unexpected JS throw) still resolves to the generic message,
 * so no code path ever renders a blank or `[object Object]`.
 */
export function resolveApiError(err: unknown, lang: Lang): ResolvedError {
  if (!(err instanceof ApiError)) {
    return { ...GENERIC_MESSAGE[lang], fieldErrors: {} };
  }

  const details = (err.details ?? {}) as ErrorDetails;
  const fieldErrors = details.issues ? resolveFieldErrors(details.issues, lang) : {};

  const base: ErrorMessage =
    (err.reason ? REASON_MESSAGES[lang][err.reason as keyof (typeof REASON_MESSAGES)[Lang]] : undefined) ??
    CODE_MESSAGES[lang][err.code] ??
    GENERIC_MESSAGE[lang];

  // A rate-limit reason can quantify the wait — fold the seconds into the hint.
  const hint =
    details.retryAfterSeconds &&
    (err.reason === "REQUEST_RATE_LIMITED" || err.reason === "REVIEW_RATE_LIMITED")
      ? retryAfterHint(details.retryAfterSeconds, lang)
      : base.hint;

  return {
    title: base.title,
    ...(hint ? { hint } : {}),
    ...(base.action ? { action: base.action } : {}),
    fieldErrors,
    ...(err.correlationId ? { correlationId: err.correlationId } : {}),
    ...(err.reason ? { reason: err.reason } : { reason: err.code }),
  };
}

/** Map a list of field issues to `{ fieldPath: message }`, most specific message wins. */
export function resolveFieldErrors(issues: FieldIssue[], lang: Lang): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issues) {
    // First issue per field wins (zod lists them in declaration order).
    if (issue.field in out) continue;
    out[issue.field] = resolveFieldMessage(issue, lang);
  }
  return out;
}

/** Resolve one field issue: field-specific override → generic rule → server message. */
export function resolveFieldMessage(issue: FieldIssue, lang: Lang): string {
  const specific = FIELD_MESSAGES[lang][issue.field]?.[issue.rule];
  if (specific) return specific;
  const generic = RULE_MESSAGES[lang][issue.rule];
  if (generic) return generic(issue.params);
  return issue.message; // English fallback from the server
}

/** Localized human label for a field path (falls back to the raw path). */
export function fieldLabelFor(field: string, lang: Lang): string {
  return FIELD_LABELS[lang][field] ?? field;
}

function retryAfterHint(seconds: number, lang: Lang): string {
  return lang === "tr" ? `${seconds} saniye sonra tekrar deneyin.` : `Try again in ${seconds} seconds.`;
}
